import React, { useEffect, useRef, useState } from "react";
import { useInView } from "../lib/useInView.js";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, LineChart, Line,
  ComposedChart, PieChart, Pie, RadarChart, Radar, PolarGrid,
  PolarAngleAxis, PolarRadiusAxis, RadialBarChart, RadialBar,
  ScatterChart, Scatter, ZAxis, XAxis, YAxis, CartesianGrid, Tooltip,
  Cell, Legend, ReferenceLine, LabelList,
} from "recharts";

// Chart palette: Apple system colours used as pops on a clean white page.
// Electric blue leads, indigo / violet / pink / teal follow. Key names are
// legacy ("coral", "gold"…) and no longer describe the hue — kept so call
// sites don't churn.
export const C = {
  coral: "#c8e64a",  // Blue — the lead series
  cyan: "#d9b892",   // Violet
  gold: "#f3ece2",   // Indigo — secondary bars
  mint: "#a9c6a0",   // Teal
  sage: "#ec9a74",   // Pink
  violet: "#9fcab6", // Mint
  slate: "#b9aa9b", red: "#ec9a74", amber: "#f2c46d",
  green: "#c8e64a",  // joiners
  ytd: "#ec9a74",    // partial-year / YTD marker (Pink pop)
};
export const SERIES = [C.coral, C.cyan, C.gold, C.mint, C.sage, C.violet];
export const FONT = "-apple-system,BlinkMacSystemFont,'SF Pro Text',"
  + "Inter,system-ui,sans-serif";
const AXIS = { stroke: "rgba(255,255,255,.95)", fontSize: 11, fontFamily: FONT,
  tickLine: false, axisLine: false };

function useIsMobile(bp = 760) {
  const [mob, setMob] = useState(() => window.innerWidth <= bp);
  useEffect(() => {
    const h = () => setMob(window.innerWidth <= bp);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, [bp]);
  return mob;
}
const GRID = "rgba(255,255,255,.10)";
const TIP = {
  contentStyle: {
    background: "rgba(74,58,46,.72)", border: "1px solid rgba(255,255,255,.18)",
    borderRadius: 16, backdropFilter: "blur(20px) saturate(180%)",
    WebkitBackdropFilter: "blur(20px) saturate(180%)",
    fontSize: 13, fontFamily: FONT, color: "#fff",
    padding: "11px 14px", letterSpacing: "-0.016em",
    boxShadow: "0 12px 40px -10px rgba(30,41,90,.28)",
  },
  labelStyle: { color: "rgba(255,255,255,.85)", marginBottom: 6, fontWeight: 500 },
  itemStyle: { color: "#fff", padding: "2px 0", fontWeight: 500 },
};

const LIME = "#c8e64a";
const PALE = "rgba(255,255,255,.38)";
const TRACK = { fill: "rgba(255,255,255,.07)", radius: 12 };

/* The floating value bubble over the highlighted bar. */
function Pill({ x, y, width, text }) {
  if (x == null || y == null) return null;
  const w = Math.max(44, String(text).length * 7.4 + 18);
  const cx = x + width / 2;
  return (
    <g>
      <rect x={cx - w / 2} y={y - 30} width={w} height={22} rx={11}
        fill="#fff" />
      <text x={cx} y={y - 15} textAnchor="middle" fill="#3a2d22" fontSize={11.5}
        fontWeight={600} fontFamily={FONT}>{text}</text>
    </g>
  );
}

/* ---- animated hero counter ---- */
function useCountUp(target, ms = 950, run = true) {
  const [v, setV] = useState(0);
  const from = useRef(0);
  useEffect(() => {
    if (!run) return;
    const start = performance.now();
    const a = from.current;
    let raf;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / ms);
      const e = 1 - Math.pow(1 - t, 3);
      setV(a + (target - a) * e);
      if (t < 1) raf = requestAnimationFrame(tick);
      else from.current = target;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, ms, run]);
  return v;
}

export function Kpi({ label, value, fmt = (x) => x, text, meta, badge,
  className = "s3", color = "#c8e64a" }) {
  const animated = typeof value === "number" && isFinite(value);
  const [ref, seen] = useInView();
  const v = useCountUp(animated ? value : 0, 950, seen);
  return (
    <div className={`card kpi ${className}` + (seen ? " in" : "")} ref={ref}
      style={{ "--c": color }}>
      <div className="kpi-top">
        <span className="chip grad" />
        <div className="label">{label}</div>
      </div>
      <div className="val">{text != null ? text
        : animated ? fmt(v) : "—"}</div>
      {(meta || badge) && (
        <div className="meta">
          {badge && <span className={`badge ${badge.kind}`}>{badge.text}</span>}
          {meta && <span>{meta}</span>}
        </div>
      )}
    </div>
  );
}

export function delta(curr, prev, suffix = "%") {
  if (prev == null || !isFinite(prev) || prev === 0) return null;
  const p = ((curr - prev) / Math.abs(prev)) * 100;
  const kind = p > 0.05 ? "up" : p < -0.05 ? "down" : "flat";
  const arrow = kind === "up" ? "▲" : kind === "down" ? "▼" : "→";
  return { kind, text: `${arrow} ${Math.abs(p).toFixed(1)}${suffix}` };
}

export function Panel({ title, sub, right, children, className = "s12" }) {
  const [ref, seen] = useInView({ rootMargin: "0px 0px -8% 0px",
    threshold: 0.02 });
  return (
    <div ref={ref} className={`panel ${className}` + (seen ? " in" : "")}>
      <div className="panel-head">
        <div>
          <h3>{title}</h3>
          {sub && <div className="panel-sub">{sub}</div>}
        </div>
        {right}
      </div>
      <div key={seen ? "live" : "held"}>{children}</div>
    </div>
  );
}

const grad = (id, color) => (
  <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
    <stop offset="0%" stopColor={color} stopOpacity={0.5} />
    <stop offset="100%" stopColor={color} stopOpacity={0.02} />
  </linearGradient>
);

export function AreaTrend({ data, x, series, h = 240 }) {
  return (
    <ResponsiveContainer width="100%" height={h}>
      <AreaChart data={data} margin={{ top: 5, right: 22, left: 0, bottom: 0 }}>
        <defs>{series.map((s, i) =>
          <React.Fragment key={s.key}>{grad(`a${i}`, SERIES[i])}</React.Fragment>
        )}</defs>
        <CartesianGrid stroke={GRID} vertical={false} />
        <XAxis dataKey={x} {...AXIS} padding={{ left: 10 }} minTickGap={12} />
        <YAxis {...AXIS} width={50} />
        <Tooltip {...TIP} />
        {series.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
        {series.map((s, i) => (
          <Area key={s.key} type="monotone" dataKey={s.key} name={s.name}
            stroke={SERIES[i]} fill={`url(#a${i})`} strokeWidth={2.4} />
        ))}
      </AreaChart>
    </ResponsiveContainer>
  );
}

export function Bars({ data, x, bars, h = 240, stacked, refLine,
  refLabel, fmt }) {
  // One series reads like the energy card: pale bars on faint tracks, the
  // latest one lit lime with its value floating above it.
  const solo = bars.length === 1 && !stacked;
  return (
    <ResponsiveContainer width="100%" height={h}>
      <BarChart data={data} barCategoryGap={stacked ? "12%" : "22%"}
        margin={{ top: solo ? 30 : 5, right: solo ? 24 : 5 }}>
        <defs>{SERIES.map((c, i) => (
          <linearGradient key={i} id={`bg${i}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={c} stopOpacity={0.95} />
            <stop offset="100%" stopColor={c} stopOpacity={0.35} />
          </linearGradient>
        ))}</defs>
        <CartesianGrid stroke={GRID} vertical={false} />
        <XAxis dataKey={x} {...AXIS} interval="preserveStartEnd" />
        <YAxis {...AXIS} width={46} tickFormatter={fmt} />
        <Tooltip {...TIP} cursor={{ fill: "rgba(255,255,255,.08)" }}
          formatter={fmt ? (v, n) => [fmt(v), n] : undefined} />
        {bars.length > 1 && <Legend wrapperStyle={{ fontSize: 12 }} />}
        {refLine != null && (
          <ReferenceLine y={refLine} stroke="#fff" strokeOpacity={0.8}
            strokeDasharray="5 4" strokeWidth={1.5}
            label={{ value: refLabel, position: "insideBottomLeft", fill: "#fff",
              fontSize: 11, fontWeight: 700, dy: -4 }} />
        )}
        {bars.map((b, i) => (
          <Bar key={b.key} dataKey={b.key} name={b.name}
            stackId={stacked ? "s" : undefined}
            fill={solo ? PALE : `url(#bg${i})`}
            background={solo ? TRACK : undefined}
            radius={stacked ? 0 : [12, 12, 12, 12]}
            maxBarSize={solo ? 44 : undefined}>
            {solo && data.map((_, j) => (
              <Cell key={j} fill={j === data.length - 1 ? LIME : PALE} />
            ))}
            {solo && <LabelList dataKey={b.key} content={(p) =>
              p.index === data.length - 1
                ? <Pill {...p} text={fmt ? fmt(p.value) : p.value} /> : null} />}
          </Bar>
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

export function Combo({ data, x, bar, line, h = 260, trimX = false, yDomain }) {
  const mob = useIsMobile();
  const xFmt = trimX && mob && data.length > 2
    ? (val, idx) => (idx === 0 || idx === data.length - 1 ? val : "")
    : undefined;
  return (
    <ResponsiveContainer width="100%" height={h}>
      <ComposedChart data={data} margin={{ top: 5, right: 14, left: 0, bottom: 0 }}>
        <CartesianGrid stroke={GRID} vertical={false} />
        <XAxis dataKey={x} {...AXIS} interval={xFmt ? 0 : "preserveStartEnd"}
          minTickGap={8} padding={{ left: 8 }} tickFormatter={xFmt} />
        <YAxis {...AXIS} width={42} domain={yDomain} allowDataOverflow={!!yDomain} />
        <Tooltip {...TIP} cursor={{ fill: "rgba(255,255,255,.08)" }} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <defs>
          <linearGradient id="cbg" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#e6dacb" stopOpacity={0.9} />
            <stop offset="100%" stopColor="#f3ece2" stopOpacity={0.3} />
          </linearGradient>
        </defs>
        <Bar dataKey={bar.key} name={bar.name} fill={PALE}
          radius={[9, 9, 9, 9]} barSize={16} background={TRACK}>
          {data.map((_, j) => (
            <Cell key={j} fill={j === data.length - 1 ? LIME : PALE} />
          ))}
        </Bar>
        {line.map((l, i) => (
          <Line key={l.key} type="monotone" dataKey={l.key} name={l.name}
            stroke={i ? C.sage : C.coral} strokeWidth={2.4} dot={false}
            strokeDasharray={l.dashed ? "5 4" : undefined} />
        ))}
      </ComposedChart>
    </ResponsiveContainer>
  );
}

export function LineTrend({ data, x, lines, h = 240 }) {
  return (
    <ResponsiveContainer width="100%" height={h}>
      <LineChart data={data} margin={{ top: 5, right: 30, left: 0, bottom: 0 }}>
        <CartesianGrid stroke={GRID} vertical={false} />
        <XAxis dataKey={x} {...AXIS} padding={{ left: 12 }} />
        <YAxis {...AXIS} width={48} />
        <Tooltip {...TIP} />
        {lines.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
        {lines.map((l, i) => (
          <Line key={l.key} type="monotone" dataKey={l.key} name={l.name}
            stroke={SERIES[i]} strokeWidth={2.4} dot={false}
            strokeDasharray={l.dashed ? "5 4" : undefined} />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

export function Donut({ data, h = 240, unit = "" }) {
  return (
    <ResponsiveContainer width="100%" height={h}>
      <PieChart>
        <Pie data={data} dataKey="value" nameKey="name" innerRadius="58%"
          outerRadius="82%" paddingAngle={3} stroke="none">
          {data.map((_, i) => (
            <Cell key={i} fill={SERIES[i % SERIES.length]} />
          ))}
        </Pie>
        <Tooltip {...TIP} formatter={(v) => `${v}${unit}`} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
      </PieChart>
    </ResponsiveContainer>
  );
}

export function RadarPanel({ data, keys, h = 280 }) {
  const mob = useIsMobile();
  return (
    <ResponsiveContainer width="100%" height={h}>
      <RadarChart data={data} outerRadius={mob ? "62%" : "72%"}
        margin={mob ? { left: 34, right: 34, top: 10, bottom: 10 } : undefined}>
        <PolarGrid stroke={GRID} />
        <PolarAngleAxis dataKey="metric" tick={(t) => {
          // Two lines on a phone so long labels ("Gross profit / bed") fit.
          const parts = mob ? String(t.payload.value).split(/ (?=\/)/) : [t.payload.value];
          return (
            <text x={t.x} y={t.y} textAnchor={t.textAnchor} fill="rgba(255,255,255,.9)"
              fontSize={11} fontFamily={FONT}>
              {parts.map((s, i) => (
                <tspan key={i} x={t.x} dy={i ? 13 : (parts.length > 1 ? -2 : 4)}>{s}</tspan>
              ))}
            </text>
          );
        }} />
        <PolarRadiusAxis tick={false} axisLine={false} />
        {keys.map((k, i) => (
          <Radar key={k.key} name={k.name} dataKey={k.key}
            stroke={SERIES[i]} fill={SERIES[i]} fillOpacity={0.22}
            strokeWidth={2} />
        ))}
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Tooltip {...TIP} />
      </RadarChart>
    </ResponsiveContainer>
  );
}

export function RadialCols({ data, h = 240, unit = "" }) {
  return (
    <ResponsiveContainer width="100%" height={h}>
      <RadialBarChart data={data} innerRadius="22%" outerRadius="100%"
        startAngle={90} endAngle={-270}>
        <RadialBar dataKey="value" cornerRadius={6} background={{ fill: "rgba(255,255,255,.12)" }}>
          {data.map((_, i) => (
            <Cell key={i} fill={SERIES[i % SERIES.length]} />
          ))}
        </RadialBar>
        <Legend iconSize={9} layout="vertical" verticalAlign="middle"
          align="right" wrapperStyle={{ fontSize: 11 }} />
        <Tooltip {...TIP} formatter={(v) => `${v}${unit}`} />
      </RadialBarChart>
    </ResponsiveContainer>
  );
}

export function Bubble({ data, h = 260, xLabel, yLabel, color,
  xTickFormatter, refY, refLabel, tipFmt }) {
  return (
    <ResponsiveContainer width="100%" height={h}>
      <ScatterChart margin={{ top: 14, right: 18, bottom: 16, left: 6 }}>
        <CartesianGrid stroke={GRID} />
        <XAxis type="number" dataKey="x" name={xLabel} {...AXIS}
          domain={xTickFormatter ? [0.5, 12.5] : ["auto", "auto"]}
          ticks={xTickFormatter
            ? [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] : undefined}
          tickFormatter={xTickFormatter} />
        <YAxis type="number" dataKey="y" name={yLabel} {...AXIS} width={46} />
        <ZAxis type="number" dataKey="z" range={[120, 1100]} />
        {/* One line per point: the default lists x, y and z separately. */}
        <Tooltip {...TIP} cursor={{ strokeDasharray: "3 3" }}
          content={tipFmt ? ({ active, payload }) => {
            if (!active || !payload?.length) return null;
            const [val, label] = tipFmt(null, null, payload[0]);
            return (
              <div style={TIP.contentStyle}>
                <div style={{ fontWeight: 700 }}>{label}</div>
                <div>{val}</div>
              </div>
            );
          } : undefined} />
        {refY != null && (
          <ReferenceLine y={refY} stroke={C.slate} strokeDasharray="5 4"
            label={{ value: refLabel, position: "insideBottomLeft", fill: "#fff",
              fontSize: 11, fontWeight: 700 }} />
        )}
        <Scatter data={data}>
          {data.map((_, i) => (
            <Cell key={i} fill={color || SERIES[i % SERIES.length]}
              fillOpacity={0.78} />
          ))}
        </Scatter>
      </ScatterChart>
    </ResponsiveContainer>
  );
}

/* Custom calendar/matrix heatmap — bespoke, not a charting default.
   Month labels sit ON TOP; every row is labelled; the actual value is
   printed faintly inside each cell so the grid is self-explanatory. */
/* On-brand data table for dense investor matrices. Config-driven so the
   same component renders the occupancy and falls grids. */
export function DataTable({ columns, rows, ytdKeys = [], emphasize = [] }) {
  const ytd = new Set(ytdKeys);
  const emph = new Set(emphasize);
  return (
    <div className="dtable-wrap">
      <table className="dtable">
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key}
                className={(c.num ? "num " : "")
                  + (ytd.has(c.key) ? "ytd" : "")}>
                {c.label}{ytd.has(c.key) && <i className="ytd-tag">YTD</i>}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, ri) => (
            <tr key={ri}
              className={emph.has(r[columns[0].key]) ? "emph" : ""}>
              {columns.map((c) => (
                <td key={c.key}
                  className={(c.num ? "num " : "")
                    + (ytd.has(c.key) ? "ytd" : "")}>
                  {r[c.key] == null || r[c.key] === ""
                    ? "—"
                    : c.fmt ? c.fmt(r[c.key]) : r[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Heatmap({ rows, cols, value, max, min = 0, unit = "",
  showCellValue = false, bands, cellFmt, vClass = "hm-v" }) {
  const pickBand = (v) => {
    for (const b of bands) if (v <= b.max) return b;
    return bands[bands.length - 1];
  };
  const ramp = (t) => {
    if (t == null) return "rgba(255,255,255,.06)";
    // Starts on the card's own neutral and warms as it climbs, so the ramp
    // sits inside the page's palette instead of fighting it.
    // Steps at 0.3 so cell numbers (white below, dark above) stay legible.
    const stops = [[0, [104, 88, 74]], [0.3, [128, 110, 96]],
      [0.3001, [196, 180, 162]], [0.5, [214, 198, 178]], [0.7, [238, 228, 212]],
      [0.86, [222, 238, 150]], [1, [200, 230, 74]]];
    const tt = Math.max(0, Math.min(1, t));
    let a = stops[0], b = stops[stops.length - 1];
    for (let i = 0; i < stops.length - 1; i++) {
      if (tt >= stops[i][0] && tt <= stops[i + 1][0]) {
        a = stops[i]; b = stops[i + 1]; break;
      }
    }
    const f = (tt - a[0]) / (b[0] - a[0] || 1);
    const c = a[1].map((v, i) => Math.round(v + (b[1][i] - v) * f));
    return `rgb(${c[0]},${c[1]},${c[2]})`;
  };
  const span = (max - min) || 1;
  return (
    <div className="hm-wrap">
      <div className="hm-x">
        <span className="hm-corner" />
        {cols.map((c) => <span key={c.key}>{c.label}</span>)}
      </div>
      <div className="hm">
        {rows.map((r) => (
          <div className="hm-row" key={r.key}>
            <div className="hm-lab">{r.label}</div>
            {cols.map((c) => {
              const v = value(r.key, c.key);
              const t = v == null ? null : (v - min) / span;
              const b = bands && v != null ? pickBand(v) : null;
              const bg = v == null ? "rgba(255,255,255,.06)"
                : bands ? b.color : ramp(t);
              const dark = bands ? (b && b.dark)
                : (t != null && t >= 0.3);
              const disp = v == null ? null
                : cellFmt ? cellFmt(v) : v;
              return (
                <div key={c.key} className="hm-cell"
                  title={v == null ? "—"
                    : `${c.label} · ${r.rowFull || r.label}: `
                      + `${disp}${unit}`}
                  style={{ background: bg,
                    color: dark
                      ? "rgba(38,30,24,.9)" : "rgba(255,255,255,.94)" }}>
                  {showCellValue && v != null
                    ? <span className={vClass}>{disp}</span> : null}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

/* X tick that adds a small "YTD" line under any in-progress FY — looked up
   from the source row rather than baked into the label string. */
function FyTick({ x, y, payload, data, xKey }) {
  const row = data.find((d) => d[xKey] === payload.value);
  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={13} textAnchor="middle" fontSize={11}
        fontFamily={FONT} fill={AXIS.stroke}>
        {payload.value}
      </text>
      {row?.ytd && (
        <text x={0} y={0} dy={26} textAnchor="middle" fontSize={9}
          fontFamily={FONT} fontWeight={600} letterSpacing={0.2}
          fill={C.ytd}>
          YTD
        </text>
      )}
    </g>
  );
}

/* Revenue (line) over EBITDA (bars) on a dual axis — annual P&L view.
   YTD bars and dots are rendered in gold to distinguish partial-year data. */
export function DualBarLine({ data, x, barKey, lineKey, barName, lineName,
  fmt = (v) => v, h = 280 }) {
  const mob = useIsMobile();
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between",
        padding: "0 58px 6px 58px" }}>
        <span className="label">{barName}</span>
        <span className="label">{lineName}</span>
      </div>
      <ResponsiveContainer width="100%" height={h}>
        <ComposedChart data={data} margin={{ top: 34, right: 6, left: 6 }}>
          <defs>
            <linearGradient id="ebg" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#e6dacb" stopOpacity={0.95} />
              <stop offset="100%" stopColor="#f3ece2" stopOpacity={0.35} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke={GRID} vertical={false} />
          <XAxis dataKey={x} {...AXIS} height={32}
            tick={<FyTick data={data} xKey={x} />} />
          <YAxis yAxisId="l" {...AXIS} width={54}
            tickFormatter={(v) => "£" + (v / 1e6).toFixed(2) + "M"} />
          <YAxis yAxisId="r" orientation="right" {...AXIS} width={54}
            tickFormatter={(v) => "£" + (v / 1e6).toFixed(2) + "M"} />
          <Tooltip {...TIP} formatter={(v, n) => [fmt(v), n]}
            cursor={{ fill: "rgba(255,255,255,.08)" }} />
          <Legend wrapperStyle={{ fontSize: 12, paddingTop: 6 }} />
          <Bar yAxisId="l" dataKey={barKey} name={barName} fill={PALE}
            strokeWidth={0} radius={[14, 14, 14, 14]} background={TRACK}
            barSize={mob ? 28 : 40}>
            {data.map((entry, i) => (
              <Cell key={i} fill={i === data.length - 1 ? LIME : PALE} />
            ))}
            <LabelList dataKey={barKey} content={(p) =>
              p.index === data.length - 1
                ? <Pill {...p} text={fmt(p.value)} /> : null} />
          </Bar>
          <Line yAxisId="r" type="monotone" dataKey={lineKey} name={lineName}
            stroke="#fff" strokeWidth={2.5}
            dot={(p) => {
              const { cx, cy } = p;
              return <circle key={`d-${cx}`} cx={cx} cy={cy} r={4.5}
                fill="#fff" stroke="rgba(90,71,56,.9)" strokeWidth={2} />;
            }}
            activeDot={{ r: 8 }} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

/* Headcount (left axis, bars) with joined / separations (right axis).
   X ticks are month-only; the spanning years are shown once at each end.
   On mobile: Joined/Separations switch from bars to lines, x-axis shows
   only first and last month labels to avoid crowding. */
export function HeadcountChart({ data, leftYear, rightYear, h = 300 }) {
  const mob = useIsMobile();
  const xFmt = mob && data.length > 2
    ? (val, idx) => (idx === 0 || idx === data.length - 1 ? val : "")
    : undefined;
  return (
    <div>
      <ResponsiveContainer width="100%" height={h}>
        <ComposedChart data={data} barCategoryGap="16%"
          margin={{ top: 8, right: 6, left: 6 }}>
          <CartesianGrid stroke={GRID} vertical={false} />
          <XAxis dataKey="x" {...AXIS} interval={0} tickMargin={8}
            tickFormatter={xFmt} />
          <YAxis yAxisId="hc" {...AXIS} width={40}
            domain={[0, "dataMax + 4"]} />
          <YAxis yAxisId="mv" orientation="right" {...AXIS} width={34}
            allowDecimals={false} domain={[0, "dataMax + 2"]} />
          <Tooltip {...TIP} cursor={{ fill: "rgba(255,255,255,.08)" }} />
          <Legend wrapperStyle={{ fontSize: 12, paddingTop: 4 }} />
          <Bar yAxisId="hc" dataKey="Headcount" name="Headcount"
            fill="rgba(255,255,255,.45)" radius={[7, 7, 0, 0]} barSize={20} />
          {mob ? (
            <>
              <Line yAxisId="mv" type="monotone" dataKey="Joined"
                name="Joined" stroke={C.green} strokeWidth={2} dot={false} />
              <Line yAxisId="mv" type="monotone" dataKey="Separations"
                name="Separations" stroke={C.red} strokeWidth={2}
                dot={false} />
            </>
          ) : (
            <>
              <Bar yAxisId="mv" dataKey="Joined" name="Joined"
                fill={C.green} radius={[4, 4, 0, 0]} barSize={9} />
              <Bar yAxisId="mv" dataKey="Separations" name="Separations"
                fill={C.red} radius={[4, 4, 0, 0]} barSize={9} />
            </>
          )}
        </ComposedChart>
      </ResponsiveContainer>
      <div style={{ display: "flex", justifyContent: "space-between",
        padding: "2px 40px 0 44px", fontSize: 12, fontWeight: 500,
        color: "var(--ink-4)", fontFamily: FONT }}>
        <span>{leftYear}</span><span>{rightYear}</span>
      </div>
    </div>
  );
}

/* Horizontal banded bars — used for the multi-year turnover trend. */
const BAND = { low: "linear-gradient(90deg,#dcef8c,#f3ece2)",
  mid: "linear-gradient(90deg,#d9b892,#ec9a74)", high: "#ec9a74" };
export function HBars({ data, max, unit = "%", refLine, refLabel }) {
  const hi = max || Math.max(...data.map((d) => d.value), 1) * 1.15;
  return (
    <div className="hbars">
      {data.map((d) => (
        <div className="hbar-row" key={d.label}>
          <div className="hbar-lab">{d.label}</div>
          <div className="hbar-track">
            <div className="hbar-fill" style={{
              width: `${Math.min(100, (d.value / hi) * 100)}%`,
              background: BAND[d.band] || C.coral }} />
            <span className="hbar-val">{d.value}{unit}</span>
          </div>
        </div>
      ))}
      {refLine != null && (
        <div className="hbar-ref" style={{
          left: `calc(126px + (100% - 126px) * ${Math.min(1, refLine / hi)})` }}>
          <span>{refLabel}</span>
        </div>
      )}
    </div>
  );
}

/* Investment-case band — the narrative spine. Each claim carries its own
   live proof metric; the lead line ties clinical and financial together. */
export function InvestmentCase({ lead, items }) {
  return (
    <div className="icase">
      <div className="icase-lead">{lead}</div>
      <div className="icase-grid">
        {items.map((it, i) => (
          <div className="icase-item" key={i}>
            <div className="icase-stat">{it.stat}</div>
            <div className="icase-label">{it.label}</div>
            <div className="icase-sub">{it.sub}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* Company-milestones timeline — vertical, year-anchored. */
export function Timeline({ items }) {
  return (
    <div className="tl">
      {items.map((it, i) => (
        <div className="tl-item" key={i}>
          <span className="tl-dot" />
          <div className="tl-year">{it.year}</div>
          <div className="tl-tag">{it.tag}</div>
          <div className="tl-text">{it.text}</div>
        </div>
      ))}
    </div>
  );
}

/* Clear occupancy readout — big %, capacity meter with 95% target,
   and a 12-month sparkline. Replaces the ambiguous radial gauge. */
export function OccupancyMeter({ pct, beds, registered, target = 95,
  spark = [] }) {
  const w = Math.max(0, Math.min(100, pct));
  return (
    <div className="occm">
      <div className="occm-top">
        <div>
          <div className="occm-big">{pct.toFixed(1)}<span>%</span></div>
          <div className="occm-sub">{beds} of {registered} beds occupied</div>
        </div>
        <div className={"occm-pill " + (pct >= target ? "ok" : "warn")}>
          {pct >= target ? "▲ above" : "▼ below"} {target}% target
        </div>
      </div>
      <div className="occm-bar">
        <div className="occm-fill" style={{ width: `${w}%` }} />
        <div className="occm-target" style={{ left: `${target}%` }}>
          <span>{target}%</span>
        </div>
      </div>
      {spark.length > 1 && (
        <ResponsiveContainer width="100%" height={70}>
          <AreaChart data={spark} margin={{ top: 6, bottom: 0 }}>
            <defs>{grad("ocs", C.coral)}</defs>
            <Tooltip {...TIP} formatter={(v) => [`${(+v).toFixed(1)}%`,
              "Occupancy"]} />
            <Area type="monotone" dataKey="v" stroke={C.coral}
              strokeWidth={2.4} fill="url(#ocs)" />
          </AreaChart>
        </ResponsiveContainer>
      )}
      <div className="occm-foot">12-month occupancy trend</div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────
   Bento feature cards
   ───────────────────────────────────────────────────────────────────── */

/* Apple Activity-style rings. `pct` is progress against the ring's own
   target (100 = one lap); anything past 100 draws a second, glowing lap so
   "ahead of benchmark" is visible at a glance. */
export function Rings({ items, size = 220, stroke = 22, gap = 6 }) {
  const [ref, seen] = useInView();
  return (
    <div className="rings" ref={ref}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}
        role="img" aria-label={items.map((r) => `${r.label} ${r.value}`)
          .join(", ")}>
        <defs>
          {items.map((r, i) => (
            <linearGradient key={i} id={`rg${i}`} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor={r.color[0]} />
              <stop offset="100%" stopColor={r.color[1]} />
            </linearGradient>
          ))}
        </defs>
        <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
          {items.map((r, i) => {
            const rad = size / 2 - stroke / 2 - i * (stroke + gap);
            const circ = 2 * Math.PI * rad;
            const p = Math.max(0, r.pct || 0);
            const lap1 = Math.min(p, 100) / 100;
            const lap2 = Math.min(Math.max(p - 100, 0), 100) / 100;
            const arc = (frac, extra) => (
              <circle cx={size / 2} cy={size / 2} r={rad} fill="none"
                stroke={`url(#rg${i})`} strokeWidth={stroke}
                strokeLinecap="round" className="ring-arc"
                strokeDasharray={circ}
                strokeDashoffset={seen ? circ * (1 - frac) : circ}
                strokeOpacity={extra ? 0.9 : 1}
                style={{ transitionDelay: `${i * 0.15 + (extra ? 1.1 : 0)}s` }} />
            );
            return (
              <g key={i}>
                <circle cx={size / 2} cy={size / 2} r={rad} fill="none"
                  stroke={r.color[1]} strokeOpacity={0.14}
                  strokeWidth={stroke} />
                {arc(lap1, false)}
                {lap2 > 0 && arc(lap2, true)}
              </g>
            );
          })}
        </g>
      </svg>
      <div className="ring-list">
        {items.map((r, i) => (
          <div className="ring-row" key={i} style={{ "--c": r.color[1] }}>
            <div className="ring-lab"><i />{r.label}</div>
            <b>{r.value}</b>
            <u>{r.note}</u>
          </div>
        ))}
      </div>
    </div>
  );
}

/* One tile per registered room, lit when occupied. Room numbers only. */
export function RoomMap({ occupied, registered }) {
  const [ref, seen] = useInView();
  const rooms = Array.from({ length: registered }, (_, i) => String(i + 1));
  const n = rooms.filter((r) => occupied.has(r)).length;
  return (
    <div ref={ref} className={seen ? "seen" : ""}>
      <div className="rooms">
        {rooms.map((r, i) => (
          <div key={r} style={{ "--i": i }}
            className={"room " + (occupied.has(r) ? "on" : "free")}
            title={`Room ${r} · ${occupied.has(r) ? "occupied" : "available"}`}>
            {r}
          </div>
        ))}
      </div>
      <div className="room-foot">
        <b>{n}<span>/ {registered}</span></b>
        <span className="label">
          {registered - n === 0 ? "every room occupied"
            : `${registered - n} room${registered - n === 1 ? "" : "s"} available`}
        </span>
      </div>
    </div>
  );
}

/* Average weekly fee across current residents, the spread from lowest to
   highest, and the average by funding type. */
export function FeeCard({ fees }) {
  const [ref, seen] = useInView();
  const n = fees.length;
  const avg = n ? fees.reduce((t, r) => t + r.fee, 0) / n : 0;
  const v = useCountUp(avg, 1200, seen);
  if (!n) return <div className="muted tiny">No fee data.</div>;
  const lo = Math.min(...fees.map((r) => r.fee));
  const hi = Math.max(...fees.map((r) => r.fee));
  const at = hi > lo ? ((avg - lo) / (hi - lo)) * 100 : 50;
  const by = {};
  for (const r of fees) (by[r.fund] ||= []).push(r.fee);
  const groups = Object.entries(by)
    .map(([k, a]) => ({ k, n: a.length, avg: a.reduce((t, x) => t + x, 0) / a.length }))
    .sort((a, b) => b.n - a.n || b.avg - a.avg);
  const g0 = (x) => "£" + Math.round(x).toLocaleString("en-GB");
  return (
    <div ref={ref}>
      <div className="fee-num">{g0(v)}<span>/ week</span></div>
      <div className="fee-sub">across {n} residents · {gbpK(avg * 52)} a year</div>
      <div className="fee-range">
        <div className="fee-track">
          <i style={{ left: seen ? `${at}%` : "0%" }} />
        </div>
        <div className="fee-ends"><span>{g0(lo)} lowest</span><span>{g0(hi)} highest</span></div>
      </div>
      <div className="fee-groups">
        {groups.map((g) => (
          <div key={g.k}>
            <span>{g.k}</span><u>{g.n}</u><b>{g0(g.avg)}</b>
          </div>
        ))}
      </div>
    </div>
  );
}
const gbpK = (x) => "£" + (x / 1000).toFixed(1) + "K";

/* A year of daily occupancy, one square per day, Monday-first weeks. */
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug",
  "Sep", "Oct", "Nov", "Dec"];
const CAL_STEPS = [
  { min: -Infinity, c: "rgba(255,255,255,.08)", l: "≤ 22" },
  { min: 23, c: "rgba(255,255,255,.28)", l: "23–24" },
  { min: 25, c: "#e9dfd0", l: "25–26" },
  { min: 27, c: "#f6f1e8", l: "27" },
  { min: 28, c: "#c8e64a", l: "28 full" },
];
export function CalendarHeat({ daily, weeks = 53 }) {
  if (!daily?.length) return null;
  const byDate = new Map(daily.map((r) => [r.date, +r.beds_occupied]));
  const last = new Date(daily[daily.length - 1].date + "T00:00:00Z");
  const dow = (last.getUTCDay() + 6) % 7; // Mon = 0
  const start = new Date(last);
  start.setUTCDate(start.getUTCDate() - dow - (weeks - 1) * 7);
  const cells = [];
  const months = [];
  for (let w = 0; w < weeks; w++) {
    for (let d = 0; d < 7; d++) {
      const dt = new Date(start);
      dt.setUTCDate(start.getUTCDate() + w * 7 + d);
      const key = dt.toISOString().slice(0, 10);
      if (d === 0 && dt.getUTCDate() <= 7) {
        months.push({ w, l: MONTHS[dt.getUTCMonth()] });
      }
      const v = dt > last ? undefined : byDate.get(key);
      const step = v == null ? null
        : [...CAL_STEPS].reverse().find((s) => v >= s.min);
      cells.push({ key, v, bg: step ? step.c : "transparent" });
    }
  }
  const full = daily.filter((r) => new Date(r.date + "T00:00:00Z") >= start
    && +r.beds_occupied >= 28).length;
  return (
    <div>
      <div className="cal-wrap">
        <div className="cal-months"
          style={{ gridTemplateColumns: `repeat(${weeks},minmax(0,1fr))` }}>
          {Array.from({ length: weeks }, (_, w) => (
            <span key={w}>{months.find((m) => m.w === w)?.l || ""}</span>
          ))}
        </div>
        <div className="cal"
          style={{ gridTemplateColumns: `repeat(${weeks},minmax(0,1fr))` }}>
          {cells.map((c) => (
            <i key={c.key} style={{ background: c.bg }}
              title={c.v == null ? c.key : `${c.key} · ${c.v} beds`} />
          ))}
        </div>
      </div>
      <div className="cal-foot">
        <span className="label">{full} days completely full in the last year</span>
        <div className="cal-scale">
          {CAL_STEPS.map((s) => (
            <React.Fragment key={s.l}>
              <i style={{ background: s.c }} /><span>{s.l}</span>&nbsp;
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}

/* Aria vs sector benchmark as gradient bullet bars; the black tick is the
   benchmark. */
export function BenchBars({ rows }) {
  const [ref, seen] = useInView();
  return (
    <div className="bench" ref={ref}>
      {rows.map((r) => {
        const hi = Math.max(r.bench * 2, (r.value || 0) * 1.05);
        const diff = r.value == null ? null
          : Math.round((r.value / r.bench - 1) * 100);
        return (
          <div className="bench-row" key={r.label}>
            <div className="bench-lab">
              <span>{r.label}</span><b>{r.value == null ? "—" : r.fmt(r.value)}</b>
            </div>
            <div className="bench-track">
              <div className="bench-fill"
                style={{ width: seen ? `${((r.value || 0) / hi) * 100}%` : 0 }} />
              <div className="bench-mark" style={{ left: `${(r.bench / hi) * 100}%` }} />
            </div>
            <div className="bench-note">
              {diff == null ? "" : `${diff >= 0 ? "+" : ""}${diff}% vs sector `}
              ({r.fmt(r.bench)})
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* Ranked list with a soft gradient bar behind each row. */
const TONE = {
  // Warm, pale fills on the taupe board — no off-palette blues or pinks.
  blue: "linear-gradient(90deg,rgba(255,255,255,.11),rgba(255,255,255,.04))",
  violet: "linear-gradient(90deg,rgba(217,184,146,.24),rgba(217,184,146,.08))",
  pink: "linear-gradient(90deg,rgba(200,230,74,.14),rgba(200,230,74,.04))",
};
export function TopList({ rows, tone = "blue" }) {
  const [ref, seen] = useInView();
  const max = Math.max(...rows.map((r) => r.value), 1);
  if (!rows.length) return <div className="muted tiny">Nothing yet.</div>;
  return (
    <div className="toplist" ref={ref}>
      {rows.map((r) => (
        <div className="top-row" key={r.label} title={r.sub || r.label}>
          <span className="top-bar" style={{ background: TONE[tone],
            width: seen ? `${(r.value / max) * 100}%` : 0 }} />
          <span className="top-lab">{r.label}</span>
          <b>{r.value.toLocaleString("en-GB")}</b>
        </div>
      ))}
    </div>
  );
}

/* One pill split into shares, with a legend underneath. */
export function SplitBar({ rows }) {
  const total = rows.reduce((s, r) => s + r.value, 0) || 1;
  const colors = ["#c8e64a", "#d9b892", "#ec9a74", "#a9c6a0"];
  return (
    <div>
      <div className="split">
        {rows.map((r, i) => (
          <span key={r.label} style={{ flex: r.value,
            background: colors[i % colors.length] }} />
        ))}
      </div>
      <div className="split-legend">
        {rows.map((r, i) => (
          <div key={r.label}>
            <i style={{ background: colors[i % colors.length] }} />
            <span>{r.label}</span>
            <b>{i === rows.length - 1 && rows.length > 1
              ? 100 - rows.slice(0, -1).reduce((t, x) =>
                t + Math.round((x.value / total) * 100), 0)
              : Math.round((r.value / total) * 100)}%</b>
          </div>
        ))}
      </div>
    </div>
  );
}

/* Thermostat-style dial: a tick-marked face with a lime arc for progress
   and a knob at its end. `pct` is 0–100 of the arc. */
export function Dial({ pct, value, label, size = 250 }) {
  const [ref, seen] = useInView();
  const c = size / 2, r = size / 2 - 16, sweep = 300, start = 120;
  const p = Math.max(0, Math.min(100, pct || 0)) / 100;
  const pt = (deg, rad) => [c + rad * Math.cos((deg * Math.PI) / 180),
    c + rad * Math.sin((deg * Math.PI) / 180)];
  const arc = (frac) => {
    const [x0, y0] = pt(start, r);
    const [x1, y1] = pt(start + sweep * frac, r);
    return `M${x0} ${y0} A${r} ${r} 0 ${sweep * frac > 180 ? 1 : 0} 1 ${x1} ${y1}`;
  };
  const len = (Math.PI * 2 * r * sweep) / 360;
  const [kx, ky] = pt(start + sweep * (seen ? p : 0), r);
  const ticks = Array.from({ length: 48 }, (_, i) => start + (sweep / 47) * i);
  return (
    <div className="dial" ref={ref}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}
        role="img" aria-label={`${label} ${value}`}>
        <circle cx={c} cy={c} r={r - 18} fill="rgba(255,255,255,.07)" />
        {ticks.map((d, i) => {
          const [x0, y0] = pt(d, r - 26), [x1, y1] = pt(d, r - (i % 4 ? 32 : 36));
          return <line key={i} x1={x0} y1={y0} x2={x1} y2={y1}
            stroke="rgba(255,255,255,.4)" strokeWidth={1.4} strokeLinecap="round" />;
        })}
        <path d={arc(1)} fill="none" stroke="rgba(255,255,255,.14)"
          strokeWidth={10} strokeLinecap="round" />
        <path d={arc(1)} fill="none" stroke={LIME} strokeWidth={10}
          strokeLinecap="round" className="ring-arc"
          strokeDasharray={len} strokeDashoffset={seen ? len * (1 - p) : len} />
        <circle cx={kx} cy={ky} r={9} fill="rgba(90,71,56,.9)" stroke={LIME}
          strokeWidth={4} style={{ transition: "all 1.6s cubic-bezier(.28,.11,.32,1)" }} />
      </svg>
      <div className="dial-mid">
        <b>{value}</b>
        <span>{label}</span>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────
   Story cards — the four questions an investor asks, each answered with
   one number, its benchmark, a plain sentence and one proof visual.
   ───────────────────────────────────────────────────────────────────── */
export function StoryCard({ q, value, bench, line, children, className = "s3" }) {
  return (
    <div className={`panel story ${className} in`}>
      <div className="story-q">{q}</div>
      <div className="story-val">{value}</div>
      {bench && <div className="story-bench">{bench}</div>}
      <div className="story-proof">{children}</div>
      <p className="story-line">{line}</p>
    </div>
  );
}

/* Small bar strip, latest bar lit lime with its value above. */
export function MiniBars({ data, fmt = (v) => v, h = 150, domain }) {
  return (
    <ResponsiveContainer width="100%" height={h}>
      <BarChart data={data} margin={{ top: 28, right: 4, left: 4, bottom: 0 }}
        barCategoryGap="22%">
        <XAxis dataKey="x" {...AXIS} interval="preserveStartEnd" height={20} />
        <YAxis hide domain={domain || [0, "auto"]} allowDataOverflow={!!domain} />
        <Tooltip {...TIP} cursor={{ fill: "rgba(255,255,255,.08)" }}
          formatter={(v) => [fmt(v), ""]} separator="" />
        <Bar dataKey="v" radius={[10, 10, 10, 10]} background={TRACK}>
          {data.map((_, j) => (
            <Cell key={j} fill={j === data.length - 1 ? LIME : PALE} />
          ))}
          <LabelList dataKey="v" content={(p) => p.index === data.length - 1
            ? <Pill {...p} text={fmt(p.value)} /> : null} />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

/* Aria against the sector, as two plain bars on one scale. */
export function VersusBars({ rows }) {
  const hi = Math.max(...rows.map((r) => r.v), 1);
  return (
    <div className="vs">
      {rows.map((r) => (
        <div className="vs-row" key={r.label}>
          <div className="vs-lab"><span>{r.label}</span><b>{r.text}</b></div>
          <div className="vs-track">
            <i style={{ width: `${Math.max(2, (r.v / hi) * 100)}%`,
              background: r.lime ? LIME : PALE }} />
          </div>
        </div>
      ))}
    </div>
  );
}
